import React, { useState } from 'react';
import { Input, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { AlertBlock, CustomHeroButton, ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { OrderActionType, OrderData } from '../types';

interface OrderActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderData | null;
  actionType: OrderActionType | null;
  onSubmit: (subscribers: number) => void;
  isSubmitting: boolean;
}

const PLATFORM_COMMISSION = 17;

const OrderActionModal: React.FC<OrderActionModalProps> = ({
  isOpen,
  onClose,
  order,
  actionType,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslations();
  const [subscribersValue, setSubscribersValue] = useState('');

  React.useEffect(() => {
    if (order) {
      setSubscribersValue(String(order.offer.subscribers));
    }
  }, [order, actionType]);

  if (!order || !actionType) return null;

  const isContinue = actionType === 'continue';
  const campaignType =
    order.campaign.type === 'free' ? t('orders.filters.campaignType.free') : t('orders.filters.campaignType.freeTrial');
  const modelLabel = order.account.username ? `@${order.account.username}` : order.account.name;
  const subscribers = Math.max(0, Number(subscribersValue) || 0);
  const pricePerSubscriber = Number(order.offer.pricePerSubscriber || 0);
  const subtotal = subscribers * pricePerSubscriber;
  const total = subtotal + (subtotal * PLATFORM_COMMISSION) / 100;
  const estimatedDays = Math.max(1, Math.ceil(subscribers / 20));
  const submitButtonTitle = isContinue ? t('orders.actions.order') : t('orders.actions.create');

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent className="md:w-[37.5rem] md:max-w-[37.5rem]">
        <ModalHeader className="px-4 py-4 md:px-6 border-b border-gray-200">
          <Typography variant="h6" className="font-medium text-xl leading-6">
            {isContinue
              ? t('orders.actions.continueHeader', { marketer: order.offer.name })
              : t('orders.actions.repeatHeader', { marketer: order.offer.name })}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 py-5 md:px-6 space-y-4">
          <Input
            label={t('orders.actions.source')}
            labelPlacement="outside"
            value={order.offer.platform}
            isReadOnly
            variant="bordered"
          />

          {!isContinue && (
            <>
              <Input
                label={t('orders.actions.campaignType')}
                labelPlacement="outside"
                value={campaignType}
                isReadOnly
                variant="bordered"
              />

              <Input
                label={t('orders.actions.model')}
                labelPlacement="outside"
                value={modelLabel}
                isReadOnly
                variant="bordered"
              />

              <Input
                label={t('orders.actions.contentLink')}
                labelPlacement="outside"
                value={order.campaign.link}
                isReadOnly
                variant="bordered"
              />
            </>
          )}

          <Input
            label={t('orders.actions.subscribers')}
            labelPlacement="outside"
            value={subscribersValue}
            onValueChange={setSubscribersValue}
            isReadOnly={!isContinue}
            type="number"
            min={1}
            variant="bordered"
          />

          <Input
            label={t('orders.actions.pricePerSubscriber')}
            labelPlacement="outside"
            value={`$${pricePerSubscriber.toFixed(2)}`}
            isReadOnly
            variant="bordered"
          />

          <Input
            label={t('orders.actions.estimatedDays')}
            labelPlacement="outside"
            value={String(estimatedDays)}
            isReadOnly
            variant="bordered"
          />

          <Input
            label={t('orders.actions.total')}
            labelPlacement="outside"
            value={`$${total.toFixed(2)}`}
            isReadOnly
            variant="bordered"
          />

          <AlertBlock
            data={[
              {
                severity: 'info',
                title: t('orders.actions.commissionInfo', { commission: PLATFORM_COMMISSION }),
                description: isContinue ? undefined : t('orders.actions.immutableInfo'),
              },
            ]}
          />
        </ModalBody>

        <ModalFooter className="px-4 py-4 md:px-6 border-t border-gray-200 gap-3">
          <CustomHeroButton
            title={t('orders.actions.cancel')}
            onPress={onClose}
            variant="secondary"
            disabled={isSubmitting}
          />
          <CustomHeroButton
            title={submitButtonTitle}
            onPress={() => onSubmit(subscribers)}
            disabled={isSubmitting || subscribers <= 0}
            isLoading={isSubmitting}
          />
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default OrderActionModal;
